import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  Typography,
  CircularProgress,
  Pagination,
  PaginationItem,
  Dialog,
  DialogTitle,
  IconButton,
  Button,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Snackbar,
  // PaginationItem,
} from "@mui/material";
// import { grey } from "@mui/material/colors";
import CloseIcon from '@mui/icons-material/Close';

// all imports
import DashboardLayout from "../../layout/DashboardLayout.js";
import ProfileView from "../profileDetails/ProfileView.js";
import { 
  searchProfiles,
  // getProfile
} from "../../services/searchService.js";
import { getLists, addProfileToList, createList } from "../../services/userService.js";
import { createCheckoutSession, redirectToCheckout } from "../../services/paymentService.js";
import ProfileCard from "./ProfileCard.js";
import { grey } from "@mui/material/colors";

function MainSearch() {
    const location = useLocation();
    const navigate = useNavigate();
  
    // Search state
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [searchText, setSearchText] = useState("");
    const [filterOptions, setFilterOptions] = useState({});
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState({});
  
    // Dialog and drawer state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [showAddToListDialog, setShowAddToListDialog] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [newListDescription, setNewListDescription] = useState('');
    const [selectedProfileForList, setSelectedProfileForList] = useState(null);
    const [lists, setLists] = useState([]);
    const [selectedList, setSelectedList] = useState('');
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showCreateListForm, setShowCreateListForm] = useState(false);
    const [buyCreditsDialogOpen, setBuyCreditsDialogOpen] = useState(false);
    const [creditsAlert, setCreditsAlert] = useState(null);
    const lastFocusedElementRef = useRef(null);

    // Credit packages
    const creditPackages = [
        { id: 1, credits: 2, price: 1, popular: false },
        { id: 2, credits: 44, price: 50, popular: true },
        { id: 3, credits: 66, price: 75, popular: false }
    ];

    // Loading animation state
    const [loadingDots, setLoadingDots] = useState('');

    // Loading animation effect
    useEffect(() => {
        if (searchLoading) {
            const interval = setInterval(() => {
                setLoadingDots(prev => {
                    if (prev === '...') return '';
                    return prev + '.';
                });
            }, 500);
            
            return () => clearInterval(interval);
        } else {
            setLoadingDots('');
        }
    }, [searchLoading]);

    // Extract search query and filters from URL parameters
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const query = params.get('q') || params.get('query') || '';
        const page = params.get('page') || 1;
        const filters = params.getAll('filters') || [];
        
        if (query) {
            setSearchText(query);
            setSelectedFilters(filters);
            // Only perform search if we're on the search page to avoid duplicate calls
            if (location.pathname === '/search') {
                performSearch(query, page, filters);
            }
        }
    }, [location.search, location.pathname]);

    const performSearch = async (query, page = 1, filters = []) => {
        if (!query.trim()) {
            setSearchResults([]);
            setTotalResults(0);
            return;
        }
        setSearchLoading(true);
    
        try {
            const response = await searchProfiles(query, page, filters);
            if (response.data.length > 0) {
                setSearchResults(response.data || []);
                setTotalResults(response.totalResults || 0);
                setFilterOptions(response.filterOptions || {});
            } else {
                setSearchResults([]);
                setTotalResults(0);
                setFilterOptions({});
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
            setTotalResults(0);
            setFilterOptions({});
        } finally {
            setSearchLoading(false);
        }
    };

    // Pagination handler
    const handlePageChange = (event, value) => {
        const params = new URLSearchParams(location.search);
        params.set("page", value);
        // Preserve existing filters when changing pages
        navigate(`${window.location.pathname}?${params.toString()}`);
    };

    // Check if user has credits
    const checkCredits = () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            if (!userInfo) {
                return false;
            }
            const user = JSON.parse(userInfo);
            const remainingCredits = user?.remainingCredits ?? 0;
            
            // Check if credits are 0 or less
            if (remainingCredits <= 0) {
                return false;
            }
            
            // Check if subscription/credits are expired (if expiry date exists)
            if (user?.subscriptionExpiry) {
                const expiryDate = new Date(user.subscriptionExpiry);
                const now = new Date();
                if (expiryDate < now) {
                    return false;
                }
            }
            
            return true;
        } catch (error) {
            console.error('Error checking credits:', error);
            return false;
        }
    };

    // Card click handler
    const handleCardClick = async (profile) => {
        // Check if user has credits
        if (!checkCredits()) {
            // Show buy credits dialog instead
            setBuyCreditsDialogOpen(true);
            return;
        }
        
        lastFocusedElementRef.current = document.activeElement;
        setSelectedProfile(profile);
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setSelectedProfile(null);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedProfile(null);
        // Return focus to last focused element after drawer closes
        setTimeout(() => {
            if (lastFocusedElementRef.current && lastFocusedElementRef.current.focus) {
                lastFocusedElementRef.current.focus();
            }
        }, 0);
    };

    const handleAddToList = async (profile, event) => {
        event.stopPropagation(); // Prevent card click
        setSelectedProfileForList(profile);
        setShowAddToListDialog(true);
        // get lists of the user
        try {
            const listsResponse = await getLists();
            if (listsResponse && listsResponse.status === 'success') {
                setLists(listsResponse.data || []);
                // If no lists exist, show create list form
                if (listsResponse.data.length === 0) {
                    setShowCreateListForm(true);
                }
            } else if (Array.isArray(listsResponse)) {
                setLists(listsResponse);
                if (listsResponse.length === 0) {
                    setShowCreateListForm(true);
                }
            } else {
                setLists([]);
                setShowCreateListForm(true);
            }
        } catch (error) {
            console.error('Failed to fetch lists:', error);
            // 401 errors are handled by API interceptor (auto redirect to login)
            // For other errors, allow user to create a list
            if (error.response && error.response.status !== 401) {
                setLists([]);
                setShowCreateListForm(true);
            }
        }
    };

    const handleCreateList = async () => {
        if (!newListName.trim()) {
            alert('Please enter a list name');
            return;
        }

        try {
            const response = await createList({ name: newListName, description: newListDescription });
            if (response.status === 'success') {
                // Refresh lists after creation
                const listsResponse = await getLists();
                if (listsResponse.status === 'success') {
                    setLists(listsResponse.data);
                    // Find the newly created list and add the profile to it
                    const newList = listsResponse.data.find(list => list.name === newListName);
                    if (newList) {
                        try {
                            await addProfileToList(newList._id, selectedProfileForList.linkedinUsername);
                            setShowSuccessAlert(true);
                            // Show the lists dropdown after successful creation
                            setShowCreateListForm(false);
                            setNewListName('');
                            setNewListDescription('');
                            setSelectedList('');
                            setTimeout(() => {
                                setShowSuccessAlert(false);
                            }, 2000);
                        } catch (error) {
                            console.error('Error adding profile to new list:', error);
                            alert('List created but failed to add profile. Please try adding manually.');
                            // Still show the lists dropdown even if adding profile failed
                            setShowCreateListForm(false);
                            setNewListName('');
                            setNewListDescription('');
                            setSelectedList('');
                        }
                    }
                }
            } else {
                alert('Error creating list: ' + response.message);
            }
        } catch (error) {
            alert('Error creating list: ' + error.message);
        }
    };

    // Filter handlers
    const handleFilterToggle = (category) => {
        setExpandedCategories(prev => {
            // If the clicked category is already expanded, collapse it
            if (prev[category]) {
                return { [category]: false };
            }
            // Otherwise, expand only this category and collapse all others
            const newState = {};
            Object.keys(filterOptions).forEach(cat => {
                newState[cat] = cat === category;
            });
            return newState;
        });
    };

    const handleFilterSelect = (filterLabel) => {
        setSelectedFilters(prev => {
            let newFilters;
            // If the same filter is clicked, deselect it
            if (prev.includes(filterLabel)) {
                newFilters = [];
            } else {
                // Only allow one filter at a time
                newFilters = [filterLabel];
            }
            
            // Update URL with new filters
            const params = new URLSearchParams(location.search);
            params.delete('filters'); // Remove existing filters
            newFilters.forEach(filter => {
                params.append('filters', filter);
            });
            params.set('page', '1'); // Reset to page 1 when filters change
            navigate(`${location.pathname}?${params.toString()}`);
            
            // Don't call performSearch here - useEffect will handle it when URL changes
            return newFilters;
        });
    };

    const handleFilterRemove = (filterLabel) => {
        setSelectedFilters(prev => {
            // Clear all filters (since only one can be selected)
            const newFilters = [];
            
            // Update URL with new filters
            const params = new URLSearchParams(location.search);
            params.delete('filters'); // Remove existing filters
            params.set('page', '1'); // Reset to page 1 when filters change
            navigate(`${location.pathname}?${params.toString()}`);
            
            // Don't call performSearch here - useEffect will handle it when URL changes
            return newFilters;
        });
    };

    const handleBuyCreditsDialogClose = () => {
        setBuyCreditsDialogOpen(false);
    };

    const handleBuyCredits = async (packageId) => {
        try {
            const selectedPackage = creditPackages.find(pkg => pkg.id === packageId);
            if (!selectedPackage) {
                setCreditsAlert({ message: 'Invalid package selected', severity: 'error' });
                return;
            }

            const response = await createCheckoutSession(selectedPackage);
            if (response.status === 'success') {
                redirectToCheckout(response.data.url);
                setBuyCreditsDialogOpen(false);
            } else {
                setCreditsAlert({ message: response.message || 'Failed to create checkout session', severity: 'error' });
            }
        } catch (error) {
            setCreditsAlert({ 
                message: error.response?.data?.message || 'An error occurred while processing payment', 
                severity: 'error' 
            });
        }
    };

    const handleContactUs = () => {
        if (typeof window !== 'undefined' && window.Tawk_API) {
            window.Tawk_API.maximize();
        }
    };

    return (
        <DashboardLayout pageName="Search">
            
            {/* Search Results Display */}
            <Box justifyContent="center" alignItems="center">
                {searchLoading ? (
                    <>
                        <Typography variant="h4" sx={{ mb: 2, color: '#333333', fontWeight: 500 }}>
                            Scanning Open Web for Signals{loadingDots}
                        </Typography>
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh"><CircularProgress size="10rem" sx={{ color: "#1BE885"}} /></Box>
                    </>
                ) : (
                    <>
                                                {/* Filter Options */}
                        {Object.keys(filterOptions).length > 0 && (
                            <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
                                {/* Header and Category Chips Side by Side */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                    <Typography variant="h6" sx={{ color: '#333', fontSize: '1rem', whiteSpace: 'nowrap' }}>
                                        Refine your search : 
                                    </Typography>
                                    
                                    {/* Category Headers in One Line */}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1 }}>
                                        {Object.entries(filterOptions).map(([category, filters]) => {
                                            if (!Array.isArray(filters) || filters.length === 0) return null;
                                            
                                            const categoryLabels = {
                                                diversity: 'Diversity',
                                                career_intent: 'Career Intent',
                                                experience_years: 'Experience Years',
                                                seniority: 'Seniority',
                                                industries: 'Industries',
                                                education: 'Education'
                                            };

                                            const isExpanded = expandedCategories[category] || false;

                                            return (
                                                <Chip
                                                    key={category}
                                                    label={categoryLabels[category] || category}
                                                    onClick={() => handleFilterToggle(category)}
                                                    variant={isExpanded ? "filled" : "outlined"}
                                                    size="small"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        height: '24px',
                                                        ...(isExpanded && {
                                                            backgroundColor: '#1BE885',
                                                            color: 'white',
                                                            '&:hover': {
                                                                backgroundColor: '#1BE885'
                                                            }
                                                        })
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                                
                                {/* Filter Options */}
                                {Object.entries(filterOptions).map(([category, filters]) => {
                                    if (!Array.isArray(filters) || filters.length === 0) return null;
                                    
                                    const isExpanded = expandedCategories[category] || false;
                                    
                                    if (!isExpanded) return null;
                                    
                                    return (
                                        <Box key={category} sx={{ 
                                            display: 'flex', 
                                            flexWrap: 'wrap', 
                                            gap: 1,
                                            p: 1,
                                            bgcolor: '#f8f8f8',
                                            borderRadius: 1
                                        }}>
                                            {filters.map((filter) => (
                                                <Chip
                                                    key={filter.label}
                                                    label={filter.anchor}
                                                    onClick={() => handleFilterSelect(filter.label)}
                                                    variant={selectedFilters.includes(filter.label) ? "filled" : "outlined"}
                                                    size="small"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        fontSize: '0.7rem',
                                                        height: '20px',
                                                        ...(selectedFilters.includes(filter.label) && {
                                                            backgroundColor: '#1BE885',
                                                            color: 'white',
                                                            '&:hover': {
                                                                backgroundColor: '#1BE885'
                                                            }
                                                        })
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                        {searchResults.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                                    {searchResults.map((profile, idx) => (
                                        <ProfileCard key={idx} profile={profile} handleAddToList={handleAddToList} handleCardClick={handleCardClick} idx={idx} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {searchResults.length === 0 && (
                            <Typography variant="h4" sx={{ mb: 2, color: '#333333', fontWeight: 500 }}>Sorry! No profiles found..</Typography>
                        )}
                        {/* Pagination */}
                        {totalResults > 0 && location && (
                            <Box paddingRight={5} display="flex" justifyContent="flex-end" my={3}>
                                <Pagination
                                    count={Math.ceil(totalResults / 10)}
                                    page={parseInt(new URLSearchParams(location.search).get("page"), 10) || 1}
                                    onChange={handlePageChange}
                                    color="primary"
                                    variant="outlined"
                                    shape="rounded"
                                    siblingCount={0}
                                    boundaryCount={0}
                                    renderItem={(item) => {
                                        // Only render previous and next arrows; hide page numbers and ellipses
                                        if (item.type === 'page' || item.type === 'start-ellipsis' || item.type === 'end-ellipsis') {
                                            return null;
                                        }
                                        
                                        // Disable next arrow if current page has less than 5 results
                                        if (item.type === 'next') {
                                            const currentPage = parseInt(new URLSearchParams(location.search).get("page"), 10) || 1;
                                            const currentPageResults = searchResults.length;
                                            return <PaginationItem {...item} disabled={currentPageResults < 5} />;
                                        }
                                        
                                        return <PaginationItem {...item} />;
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
            {/* Search Results Display */}

            {/* Profile Detail Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth='lg' PaperProps={{sx: { width: '80vw', height: '85vh', marginTop: '10vh', marginLeft: '20vw', marginRight: '20vw', borderRadius: 1, zIndex: 9998, backgroundColor: grey[200] }}}>
                <DialogTitle sx={{ m: 1, pt: 4 }} id="customized-dialog-title" />
                <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 25, top: 25, color: "#fffff" }}>
                    <CloseIcon />
                </IconButton>
                <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                    {selectedProfile && (
                        <ProfileView profileData={selectedProfile} />
                    )}
                </Box>
            </Dialog>
            {/* Profile Detail Dialog */}
        
            {/* Add to List Dialog */}
            <Dialog open={showAddToListDialog} onClose={() => setShowAddToListDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { zIndex: 9998 } }}>
                <DialogTitle>Add "{selectedProfileForList?.firstName} {selectedProfileForList?.lastName}" to a list</DialogTitle>
                <IconButton 
                    aria-label="close" 
                    sx={{ position: 'absolute', right: 8, top: 8, color: '#fffff' }}
                    onClick={() => {
                        setShowAddToListDialog(false);
                        setSelectedList('');
                        setShowSuccessAlert(false);
                        setShowCreateListForm(false);
                        setNewListName('');
                        setNewListDescription('');
                    }}
                ><CloseIcon /></IconButton>
                <DialogContent>
                
                {/* Success Alert */}
                {showSuccessAlert && (
                    <Alert severity="success" sx={{ mb: 1 }}>
                        {showCreateListForm ? 'List created and profile added successfully!' : 'Profile added to list successfully!'}
                    </Alert>
                )}
                {/* Success Alert */}
                
                {/* Create New List Form */}
                {showCreateListForm && (
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, color: '#666' }}>Create New List</Typography>                        
                        <TextField fullWidth label="List Name" value={newListName} onChange={(e) => setNewListName(e.target.value)} sx={{ mb: 1 }} required />
                        <TextField fullWidth label="Description (Optional)" value={newListDescription} onChange={(e) => setNewListDescription(e.target.value)} sx={{ mb: 1}} />
                        <Button variant="contained" onClick={handleCreateList} sx={{ mr: 1, backgroundColor: '#1BE885', color: '#fff' }}>Create List</Button>
                        <Button variant="outlined" onClick={() => {setShowCreateListForm(false); setNewListName(''); setNewListDescription('');}}>Cancel</Button>
                    </Box>
                )}
                {/* Create New List Form */}
          
                {/* Existing Lists Section */}
                {lists.length > 0 && !showCreateListForm && (
                    <Box sx={{ mb: 2 }}>
                        <FormControl fullWidth sx={{ mb: 1, mt: 1 }}>
                            <InputLabel>Select a list</InputLabel>
                            <Select
                                value={selectedList}
                                onChange={(e) => {
                                    if (e.target.value === 'create_new') {
                                        setShowCreateListForm(true);
                                        setSelectedList('');
                                    } else {
                                        setSelectedList(e.target.value);
                                    }
                                    }} 
                                label="Select a list" 
                            >
                                {lists.map((list) => (
                                    <MenuItem key={list._id} value={list._id}>{list.name}</MenuItem>
                                ))}
                                <Divider sx={{ my: 1 }} />
                                <MenuItem value="create_new" sx={{ fontWeight: 'bold' }}>+ Create a new list</MenuItem>
                            </Select>
                        </FormControl>
              
                        {selectedList && (
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: '#1BE885', color: '#fff' }}
                                onClick={async () => {
                                    try {
                                        const response = await addProfileToList(selectedList, selectedProfileForList.linkedinUsername);
                                        setShowSuccessAlert(true);
                                        setTimeout(() => {
                                            setShowSuccessAlert(false);
                                            setShowAddToListDialog(false);
                                            setSelectedList('');
                                        }, 2000);
                                    } catch (error) {
                                        console.error('Error adding profile to list:', error);
                                    }
                                }}
                            >
                                Add to Selected List
                            </Button>
                        )}
                    </Box>
                )}
            </DialogContent>
            </Dialog>
            {/* Add to List Dialog */}

            {/* Buy Credits Dialog */}
            <Dialog open={buyCreditsDialogOpen} onClose={handleBuyCreditsDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>Buy Credits</Typography>
                            <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mt: 1 }}>
                                You need credits to view profile details. Choose a credit package that suits your needs.
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleBuyCreditsDialogClose}
                            sx={(theme) => ({
                                color: theme.palette.grey[500],
                            })}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mt: 2 }}>
                        {creditPackages.map((creditPackage) => (
                            <Card
                                key={creditPackage.id}
                                sx={{
                                    minWidth: 200,
                                    border: creditPackage.popular ? '2px solid #1ce484' : '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    position: 'relative',
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-2px)',
                                        transition: 'all 0.2s ease-in-out'
                                    }
                                }}
                            >
                                {creditPackage.popular && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: -10,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        bgcolor: '#1ce484',
                                        color: '#000',
                                        px: 1,
                                        py: 1,
                                        borderRadius: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                    }}>Most Popular</Box>
                                )}
                            
                                <CardContent sx={{ textAlign: 'center', pt: creditPackage.popular ? 3 : 2 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1ce484', mb: 1 }}>{creditPackage.credits}</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Credits</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>${creditPackage.price}</Typography>
                                    <Button variant="contained" fullWidth onClick={() => handleBuyCredits(creditPackage.id)} sx={{ bgcolor: '#1ce484', color: '#000', fontWeight: 600, '&:hover': { bgcolor: '#20ec84' }}}>
                                        Buy Now
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                        
                        {/* Contact Us Card */}
                        <Card
                            sx={{
                                minWidth: 200,
                                border: '2px solid #3b82f6',
                                borderRadius: 2,
                                position: 'relative',
                                '&:hover': {
                                    boxShadow: 4,
                                    transform: 'translateY(-2px)',
                                    transition: 'all 0.2s ease-in-out'
                                }
                            }}
                        >
                            <CardContent sx={{ textAlign: 'center', pt: 2 }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6', mb: 1 }}>Customize</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Credits</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Contact Us</Typography>
                                <Button 
                                    variant="contained" 
                                    fullWidth 
                                    onClick={handleContactUs} 
                                    sx={{ 
                                        bgcolor: '#3b82f6', 
                                        color: '#fff', 
                                        fontWeight: 600, 
                                        '&:hover': { bgcolor: '#2563eb' }
                                    }}
                                >
                                    Contact Us
                                </Button>
                            </CardContent>
                        </Card>
                    </Box>
                </DialogContent>
            </Dialog>
            {/* Buy Credits Dialog */}

            {/* Credits Alert Snackbar */}
            <Snackbar open={creditsAlert !== null} autoHideDuration={6000} onClose={() => setCreditsAlert(null)}>
                <Alert onClose={() => setCreditsAlert(null)} severity={creditsAlert?.severity}>{creditsAlert?.message}</Alert>
            </Snackbar>
            {/* Credits Alert Snackbar */}

        </DashboardLayout>
  );
}

export default MainSearch;